import { DeleteOutlineRounded } from "@mui/icons-material";
import { CircularProgress, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Grid from "@mui/system/Unstable_Grid";
import { addDays, subMonths } from "date-fns";
import { isEmpty, map } from "lodash";
import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { ArtImage } from "../../../components/ArtImage";
import { useDialogConfirmation } from "../../../hooks/useDialogConfirmation";
import { useToast } from "../../../hooks/useToast";
import { queryClient } from "../../../services/queryClient";
import { createAdvertising, listArts } from "../../../services/studio";
import { Arts, RequestArtParams } from "../../../types/studio";
import { LIGHT_BLACK, RED } from "../../../utils/colors";

export function Gallery() {
  const navigate = useNavigate();
  const toast = useToast();
  const dialog = useDialogConfirmation();

  const rangeDate = useMemo(() => {
    return {
      endDate: addDays(new Date(), 1),
      startDate: subMonths(new Date(), 1),
    };
  }, []);

  const arts = useQuery("arts", async () => {
    const params: RequestArtParams = {
      endDate: rangeDate.endDate,
      startDate: rangeDate.startDate,
    };

    return listArts(params);
  });

  const newAdvertising = useMutation(
    async (data: any) => {
      toast.show("Preparando propaganda...", { severity: "info" });
      const response = await createAdvertising(data);
      return response;
    },
    {
      onSuccess: (advertising) => {
        queryClient.invalidateQueries("arts");
        toast.show("Propaganda criada com sucesso!", { severity: "success" });
        navigate(String(advertising.id_tabloide_digital), {
          state: { name: advertising.nome_tabloide_digital },
        });
      },
      onError: () => {
        toast.show("Não foi possível criar a propaganda", {
          severity: "error",
        });
      },
    }
  );

  const handleStudioFolder = useCallback(
    (art: Arts) => {
      navigate(String(art.id_tabloide_digital), {
        state: { name: art.nome_tabloide_digital },
      });
    },
    [navigate]
  );

  const handleNewAdvertising = useCallback(async () => {
    await newAdvertising.mutateAsync(rangeDate);
  }, [newAdvertising, rangeDate]);

  const handleDelete = useCallback(
    (id: number) => {
      dialog.show(
        "Excluir artes?",
        "Tem certeza que deseja excluir esta pasta de propagandas?",
        {
          onConfirmate: () => {
            console.log(id);
            dialog.close();
          },
        }
      );
    },
    [dialog]
  );

  const RenderOverlay = (data: Arts) => {
    return (
      <Stack direction="row" justifyContent="space-between">
        <Button
          disableElevation
          size="small"
          variant="contained"
          color="inherit"
          sx={{ color: LIGHT_BLACK }}
        >
          Duplicar
        </Button>
        <Button
          onClick={() => {
            handleDelete(data.id_tabloide_digital);
          }}
          disableElevation
          size="small"
          variant="contained"
          color="inherit"
          sx={{ color: RED, minWidth: 0 }}
        >
          <DeleteOutlineRounded />
        </Button>
      </Stack>
    );
  };

  return (
    <Stack spacing={2}>
      <Stack justifyContent="space-between" direction="row">
        <Stack spacing={2} direction="row">
          <TextField
            disabled={arts.isLoading || newAdvertising.isLoading}
            label="Pesquisar por nome"
            size="small"
          />
          <TextField
            disabled={arts.isLoading || newAdvertising.isLoading}
            label="Pesquisar por ID"
            size="small"
          />
        </Stack>
        <Button
          onClick={handleNewAdvertising}
          disabled={arts.isLoading || newAdvertising.isLoading}
          size="small"
          color="primary"
          variant="contained"
        >
          Criar nova propaganda
        </Button>
      </Stack>
      <Grid container spacing={2}>
        {arts.isLoading ? (
          <Grid xs={12} justifyContent="center" sx={{ display: "flex" }}>
            <CircularProgress />
          </Grid>
        ) : isEmpty(arts.data) ? (
          <Grid xs={12} justifyContent="center" sx={{ display: "flex" }}>
            <Typography color="GrayText">Lista de artes vazia.</Typography>
          </Grid>
        ) : (
          map(arts.data, (art, index) => (
            <Grid xs={2} key={index}>
              <ArtImage
                overlayComponent={<RenderOverlay {...art} />}
                onClick={() => handleStudioFolder(art)}
                image={art.artes[0]?.miniatura || art.artes[0]?.link}
                name={art.nome_tabloide_digital}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Stack>
  );
}
